import dayjs from "dayjs";
import BioExamPrescribedJournalEvent from "parog-web/models/bio-exam-prescribed-journal-event";
import GenericPrescriptionUploadedJournalEvent from "parog-web/models/generic-prescription-uploaded-journal-event";
import MedicalImagingPrescribedJournalEvent from "parog-web/models/medical-imaging-prescribed-journal-event";
import MedicationPrescribedJournalEvent from "parog-web/models/medication-prescribed-journal-event";
import ParamedicalCarePrescribedJournalEvent from "parog-web/models/paramedical-care-prescribed-journal-event";
import type PrescriptionJournalEvent from "parog-web/models/prescription-journal-event";
import TlsPrescribedJournalEvent from "parog-web/models/tls-prescribed-journal-event";
import { getPrescriptionJournalEventComponent } from "./prescription-helpers";

/**
 * This class abstract the "xxxx-prescribed-journal-event"
 * It's a kind of decorator which allows the template to manipulate
 * only one kind of object.
 */
export default class Prescription<T extends PrescriptionJournalEvent> {
  declare description: string;
  declare editionFormId: string;
  declare isImported?: boolean;
  journalEvent: T;
  declare name: string;
  occurredAt: Date;
  pdfUrl: string;
  prescriptionComponent: string | undefined;

  get endDate(): Date | undefined {
    return this.journalEvent.endDate;
  }

  get startDate(): Date {
    return this.journalEvent.startDate;
  }

  constructor(journalEvent: T) {
    this.journalEvent = journalEvent;

    this.occurredAt = journalEvent.occurredAt;
    this.pdfUrl = `${journalEvent.pdfUrl}`;
    this.prescriptionComponent = getPrescriptionJournalEventComponent(
      this.journalEvent
    );

    this.initWithJournalEvent(journalEvent);
  }

  initWithJournalEvent(journalEvent: T): void {
    if (journalEvent instanceof BioExamPrescribedJournalEvent) {
      this.editionFormId = "prescription/bio-exam-edition-form";
      this.name = `Examens biologiques`;
      this.description =
        this.name + " du " + dayjs(this.occurredAt).format("DD/MM/YYYY");
      this.isImported = journalEvent.fileAttachments.length !== 0;
    } else if (journalEvent instanceof MedicationPrescribedJournalEvent) {
      this.editionFormId = "prescription/medication-edition-form";
      this.name = "Ordonnance médicamenteuse";
      this.description =
        this.name + " du " + dayjs(this.occurredAt).format("DD/MM/YYYY");
      this.isImported = journalEvent.fileAttachments.length !== 0;
    } else if (journalEvent instanceof TlsPrescribedJournalEvent) {
      this.editionFormId = "tls-prescription-edition-form";
      this.name = "Ordonnance de télésurvéillance";
      this.description =
        this.name +
        " valide jusqu'au " +
        dayjs(journalEvent.endDate).format("DD/MM/YYYY");
    } else if (journalEvent instanceof MedicalImagingPrescribedJournalEvent) {
      this.editionFormId = "prescription/medical-imaging-edition-form";
      this.name = "Ordonnance d'imagerie ou d'examen médical";
      this.description =
        this.name +
        (journalEvent.fileAttachments.length !== 0
          ? " du " + dayjs(journalEvent.occurredAt).format("DD/MM/YYYY")
          : " valide jusqu'au " +
            dayjs(journalEvent.endDate).format("DD/MM/YYYY"));
      this.isImported = journalEvent.fileAttachments.length !== 0;
    } else if (journalEvent instanceof ParamedicalCarePrescribedJournalEvent) {
      this.editionFormId = "prescription/paramedical-care-edition-form";
      this.name = "Ordonnance de soins paramédicaux";
      this.description =
        this.name +
        (journalEvent.fileAttachments.length !== 0
          ? " du " + dayjs(journalEvent.occurredAt).format("DD/MM/YYYY")
          : " valide jusqu'au " +
            dayjs(journalEvent.endDate).format("DD/MM/YYYY"));
      this.isImported = journalEvent.fileAttachments.length !== 0;
    } else if (
      journalEvent instanceof GenericPrescriptionUploadedJournalEvent
    ) {
      this.editionFormId = "";
      this.name = "Ordonnance";
      this.description =
        this.name +
        " reçue le " +
        dayjs(journalEvent.occurredAt).format("DD/MM/YYYY");
    } else {
      throw new TypeError(
        `The journal event passed as parameter is not a prescription : ${journalEvent}`
      );
    }
  }
}
